import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResource } from '@app/src/api/fetchHooks'
import Permissions, { usePermissions } from '@app/src/auth/permissions'
import type { OrganizationRiskSetting } from '@app/src/types/resourceExplorer'
import { RiskIndexType } from '@app/src/types/resourceExplorer'
import { keepPreviousData } from '@tanstack/react-query'
import type { FC, ReactNode } from 'react'
import React, { useMemo } from 'react'
import { useAccount } from './AccountContext'
import type { RiskSettingsContextProps } from './RiskSettingsContext'
import RiskSettingsContext from './RiskSettingsContext'

type RiskSettingsContextProviderProps = {
  children: ReactNode
}

const RiskSettingsContextProvider: FC<RiskSettingsContextProviderProps> = ({
  children,
}: RiskSettingsContextProviderProps) => {
  const { hasPermission } = usePermissions()
  const { account } = useAccount()

  const {
    data: riskSettings = [],
    isLoading,
    isError,
  } = useFetchResource<OrganizationRiskSetting[]>({
    key: FetchKey.OrganizationRiskSettings,
    endpoint: endpoints.organizationRiskSettings,
    options: {
      enabled: Boolean(account?.organization) && hasPermission(Permissions.RISK_ACCESS),
      placeholderData: keepPreviousData,
    },
  })

  const activeRiskSettings =
    useMemo(
      () => riskSettings?.filter(setting => setting.isActivated).map(riskSetting => riskSetting.riskType),
      [riskSettings],
    ) ?? []

  const activeCountryRiskSettings = useMemo(
    () => activeRiskSettings.filter(risk => risk.riskIndexType === RiskIndexType.Country),
    [activeRiskSettings],
  )

  const activeIndustryRiskSettings = useMemo(
    () => activeRiskSettings.filter(risk => risk.riskIndexType === RiskIndexType.Industry),
    [activeRiskSettings],
  )

  const getCountryRisksForSustainabilityArea: RiskSettingsContextProps['getCountryRisksForSustainabilityArea'] = (
    countryRisks = [],
    sustainabilityArea,
  ) => {
    return countryRisks
      .filter(risk => risk.riskType.sustainabilityArea === sustainabilityArea)
      .filter(risk => activeRiskSettings?.find(riskSetting => riskSetting.id === risk.riskType.id))
  }

  return (
    <RiskSettingsContext.Provider
      value={{
        riskSettings,
        activeRiskSettings,
        activeCountryRiskSettings,
        activeIndustryRiskSettings,
        isLoadingRiskSettings: isLoading,
        isErrorRiskSettings: isError,
        getCountryRisksForSustainabilityArea,
      }}
    >
      {children}
    </RiskSettingsContext.Provider>
  )
}

export default RiskSettingsContextProvider
